import text from './text.json'
import data from './data.json'
import './index.scss'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import {BigCheckIcon, UpgradeIcon, StatuspageIcon, UptimeMonitoringIcon, ClosedIncidentManagementIcon} from '../../icons'
import Footer from '../../components/layouts/footer'
import ContentLoader from '../../components/core/loader'
import {ImgButton} from '../../components/core/button'
import {createSession, getSubscriptions} from '../../stores/payment'
import {getPlans, getPlanLimits} from '../../stores/plans'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useSearchParams, useLocation, Link} from 'react-router-dom'
import {useEffect, useRef, useState} from 'react'
import AmountManager from './../../utils/amount'
import FullscreenLoader from '../../components/core/loader/fullcreenLoader'
import Modal from '../../components/core/modal'
import UpgradeModal from './UpgradeModal'
import LimitManager from '../../utils/limitManager'
import Badge from '../../components/core/badge'
import Customer from '../../utils/customer'
import localStorageManager from '../../utils/localStorageManager'
import localStorageKeys from '../../utils/localStorageKeys'
import moment from 'moment'
import {colors} from '../../utils/colors'
import CustomSelect from '../../components/core/customSelect'
import renderSkeletonLoaders from '../../utils/loader'
import classNames from 'classnames'
import plansData from '../../data/plans.json'
import {isSelfHosted} from '../../utils/newFeature'

const MONTH_INTERVAL = 'month'
const YEAR_INTERVAL = 'year'
const EUR_CURRENCY = 'eur'
const MONITORING_HASH = '#monitoring'
const PLAN = 'plan'
const CURRENCY = 'currency'
const DISABLED_PLAN_TEXT_FEATURE = '-'
const LIMIT_TYPES = {
    count: 'count',
    feature: 'feature'
}

const allPlans = Object.values(plansData)

const Plans = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const {hash} = useLocation()
    const [searchParams] = useSearchParams()
    const planRef = useRef()
    const [paymentInterval, setPaymentInterval] = useState(MONTH_INTERVAL)
    const [showLoader, setShowLoader] = useState(false)
    const [isModalActive, setIsModalActive] = useState(false)
    const [defaultCurrency, setDefaultCurrency] = useState(null)
    const fetchedPlans = useSelector(state => state.plans.plans)
    const plansLimits = useSelector(state => state.plans.plansLimits)
    const isFetching = useSelector(state => state.plans.isFetching)
    const {coupon, subscriptions} = useSelector(state => state.payment)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const currentPlan = localStorage.getItem(localStorageKeys.PLAN)
    const searchedPlan =  searchParams.get(PLAN)
    const searchedCurrency = searchParams.get(CURRENCY)
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated)
    const isTrialExpired = new Date(localStorageManager.get(localStorageKeys.TRIAL)?.expires) < new Date()
    const trialExpiresAt = moment(localStorageManager.get(localStorageKeys.TRIAL)?.expires).format('YYYY/MM/DD')
    const isTrialPeriod = !!LimitManager.isTrialPeriod() && !isTrialExpired
    const isTrialUsed = localStorageManager.get(localStorageKeys.TRIAL) !== null && !isTrialPeriod
    const isCurrentPlanAppsumo = currentPlan?.includes(plansData.appsumo_key)
    const selectedCurrency = localStorage.getItem(localStorageKeys.SELECTED_CURRENCY)
    const appsumoPlanChangeUrl = 'https://appsumo.com/account/redemption/:invoice_item_uuid#change-plan'
    const currentSection = data.plans_section_names.find(section => section.hash === hash)
    const filteredPlansSections = currentSection ? data.plans_section_names.filter(section => section !== currentSection) : data.plans_section_names

    const appsumoUserPlans = data.appsumo_user_plans.filter(plan => plan.id !== currentPlan ? !plan.id.includes(plansData.appsumo_key) : plan)
    const plans = isCurrentPlanAppsumo ? appsumoUserPlans : data.user_plans

    const getPriceSymbol = defaultCurrency === EUR_CURRENCY ? '€' : '$'

    const returnFeatureValue = val => {
        return typeof (val) === 'boolean' ? val ? <BigCheckIcon/> : <BigCheckIcon stroke={colors.colorLightGrey}/> : val
    }

    const handleChange = e => {
        e.target.checked ? setPaymentInterval('year') : setPaymentInterval('month')
    }

    const findPlanPriceId = (key, interval) => {
        return fetchedPlans?.find(fetchedPlan => fetchedPlan?.plan === key && fetchedPlan?.interval === interval && fetchedPlan?.currency === defaultCurrency)?.product_id
    }

    const showUpgradingDetails = async(priceId) => {
        setIsModalActive(priceId)
    }

    const getUpgradeIsEnabled = key => {
        const index = allPlans.indexOf(allPlans?.find(item => item?.key === key))
        const currentPlanIndex = allPlans.indexOf(allPlans?.find(item => item?.key === currentPlan))
        return index > currentPlanIndex && currentPlanIndex !== -1
    }

    const upgradePlan = (key, interval, isTrial = false) => {
        if (key.includes(plansData.appsumo_key)) {
            window.open(appsumoPlanChangeUrl.replace(':invoice_item_uuid', localStorage.getItem(localStorageKeys.APPSUMO_INVOICE_UUID)), '_blank')
            return
        }
        const priceId = findPlanPriceId(key, interval)
        localStorage.setItem('upgradedPlan', key)
        if (currentPlan === plansData.free_v1.key || currentPlan === plansData.free_v2.key || isCurrentPlanAppsumo || !subscriptions?.data?.subscriptions) {
            dispatch(createSession(setShowLoader, {
                price_id: priceId,
                coupon_code: localStorage.getItem('coupon'),
                is_trial: isTrial
            }))
            localStorage.removeItem('coupon')
            return
        }
        showUpgradingDetails({
            priceId,
            key
        })
    }

    const findPlanData = (key, interval, currency) => {
        return fetchedPlans?.find(fetchedPlan => fetchedPlan?.plan === key && fetchedPlan?.interval === interval && fetchedPlan?.currency === currency)
    }

    const getDiscountedPrice = price => {
        let discountedPrice
        if (coupon?.discount_amount) {
            discountedPrice = price - coupon?.discount_amount
        }

        if (coupon?.discount_percentage) {
            discountedPrice = price - (price * coupon?.discount_percentage / 100)
        }

        return `<span>${AmountManager.normalizePrice(price, getPriceSymbol)}</span> ${AmountManager.normalizePrice(discountedPrice, getPriceSymbol)}`
    }

    const getNormalizedPrice = plan => {
        const price = AmountManager.returnMonthlyAmount(plan?.amount, plan?.interval)
        if (coupon?.price_ids?.includes(plan?.product_id)) {
            return getDiscountedPrice(price)
        }
        return `${AmountManager.normalizePrice(price, getPriceSymbol)}`
    }

    const getMonthlyPrice = (plan, interval, currency) => {
        const planData = findPlanData(plan.id, interval, currency)
        return getNormalizedPrice(planData)
    }

    const getAppsumoTierPrice = key => {
        return data.appsumo_tiers.find(tier => tier.key === key).price
    }

    const fetchPlans = async() => {
        await dispatch(getPlans())
    }

    const getTrialButtonIsEnabled = plan => {
        return (currentPlan ===  plansData.free_v2.key && !isTrialUsed) || (!isTrialUsed && isTrialPeriod && currentPlan !== plan.id && subscriptions?.data?.subscriptions)
    }

    const generatePlansButton = () => {
        return (
            <>
                {plans.map(item => (
                    <div className="bg-blue" key={item.key}>
                        {
                            (!isTrialPeriod && currentPlan === item.id) || (!currentPlan && item.isEnterprise) ? (
                                <ImgButton className="outline responsive-button">
                                    {text.current}
                                </ImgButton>
                            ) : item?.isEnterprise && !isSelfHosted() ?
                                <Link to={`mailto:${text.email_address}`}>
                                    <ImgButton>
                                        {item.contact_us}
                                    </ImgButton>
                                </Link> : (
                                    !isSelfHosted() && <div className="d-flex flex-column gap-half align-items-center">
                                        {getTrialButtonIsEnabled(item) ? (
                                            <ImgButton disabled={!getUpgradeIsEnabled(item.id) && !item.id.includes(plansData.appsumo_key)} onClick={() => upgradePlan(item.id, paymentInterval, true)} className="light-blue" planRef={item.key.includes(searchedPlan) ? planRef : null} plans={fetchedPlans}>
                                                {text.try_for_free}
                                            </ImgButton>
                                        ) : isTrialPeriod && currentPlan === item.id && (
                                            <>
                                                <Badge color="muted" size="big">
                                                    {text.trial_period}
                                                </Badge>
                                                <span className={classNames('expires', {
                                                    dark: isDarkMode
                                                })}>{`${text.expires}: ${trialExpiresAt}`}</span>
                                            </>
                                        )}
                                        {(!isTrialPeriod || isTrialUsed || (isTrialPeriod && !subscriptions?.data?.subscriptions && currentPlan !== item.id)) && (
                                            <ImgButton
                                                disabled={(!Customer.isRoot() || !isTrialPeriod && !getUpgradeIsEnabled(item.id)) && !item.id.includes(plansData.appsumo_key) || (isTrialPeriod && !getUpgradeIsEnabled(item.id))}
                                                onClick={() => upgradePlan(item.id, paymentInterval)}>
                                                {(isTrialPeriod || getUpgradeIsEnabled(item.id)) &&
                                            <UpgradeIcon className="upgrade-icon"/>}
                                                {(isTrialPeriod && getUpgradeIsEnabled(item.id)) ?
                                                    text.upgrade :
                                                    (isTrialPeriod || !getUpgradeIsEnabled(item.id)) ?
                                                        text.downgrade :
                                                        text.upgrade}
                                            </ImgButton>
                                        )}
                                    </div>
                                )
                        }
                    </div>
                ))}
            </>
        )
    }

    const generatePlansPrice = () => {
        return plans.map(item => (
            <div className="bg-blue" key={item.key}>
                {!isSelfHosted() && (!fetchedPlans.length ? renderSkeletonLoaders(1, 'text-skeleton') :
                    item?.isEnterprise ?
                        <p>{DISABLED_PLAN_TEXT_FEATURE}</p> :
                        <p className="text-parts">
                            <span
                                dangerouslySetInnerHTML={{__html: item.id.includes(plansData.appsumo_key) ? getAppsumoTierPrice(item.id) : getMonthlyPrice(item, paymentInterval, defaultCurrency)}}/>{`/${item.id.includes(plansData.appsumo_key) ? text.lifetime : text.month}`}</p>)}
            </div>
        ))
    }

    const returnPlansSection = (section) => {
        return (
            <div key={section.key} className={classNames('plans-header', {
                dark: isDarkMode
            })}>
                <div className="plans-header-top">
                    <div className={classNames('no-center plan-title', {
                        'self-hosted': isSelfHosted()
                    })}>
                        {getIconBySection(section.key, colors)}
                        <p className="left-sub-title">{section.name}</p>
                    </div>
                    {returnPlansData(plansLimits?.[section.key])?.map((plan, index) => (
                        <div key={index} className={classNames('plans-cost bg-blue', {
                            'self-hosted': isSelfHosted()
                        })} />
                    ))}
                </div>
                {sortAndExtractKeysAndTitles(plansLimits?.[section.key]?.[0]?.limits)?.map((feature, index) => (
                    <div key={index} className="plans-header-top">
                        <div className="no-center">
                            <p className="left-items" dangerouslySetInnerHTML={{__html: feature.name}}/>
                        </div>
                        {returnPlansData(plansLimits?.[section.key])?.map((plan, index) => (
                            <div key={index} className="bg-blue">
                                <p>{returnFeatureValue(plan[feature.key])}</p>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        )
    }

    const getIconBySection = (sectionKey) => {
        const sectionIcons = {
            monitoring: <UptimeMonitoringIcon stroke={colors.colorBlue} />,
            statuspage: <StatuspageIcon stroke={colors.colorBlue} />,
            incident_management: <ClosedIncidentManagementIcon strokeColor={colors.colorBlue} strokeWidth={0.397222} width={27.8} height={19}/>
        }
        return sectionIcons[sectionKey]
    }

    const sortAndExtractKeysAndTitles = list => {
        const filteredList = list?.filter(item => item?.show === true)
        const transformedList = filteredList?.map(item => ({
            key: item?.key,
            name: item?.title
        }))
        return transformedList
    }

    const returnLimits = limits => {
        const filteredList = limits?.filter(item => item?.show === true)
        const result = {}

        filteredList.forEach(item => {
            if (item.type === LIMIT_TYPES.count) {
                result[item.key] = item.value_to_show ? item?.value_to_show : item.value === 0 ? DISABLED_PLAN_TEXT_FEATURE : item?.value
            }

            if (item.type === LIMIT_TYPES.feature) {
                result[item.key] = item.value === 1
            }
        })

        return result
    }

    const returnPlansData = list => {
        const transformedList = list?.map(item => {
            const limits = returnLimits(item?.limits)
            const listItem = plans.find(initialListItem => initialListItem.id === item.plan_id)
            return {
                ...limits,
                name: listItem?.name,
                desc: listItem?.desc
            }
        })
        return transformedList
    }

    const returnCurrentPlanBadge = (plan) => isSelfHosted() && allPlans?.find(planItem => planItem.key === currentPlan)?.display_name === plan.name && <Badge color="primary">{text.current}</Badge>

    const selectDefaultCurrency = () => {
        if (selectedCurrency) {
            setDefaultCurrency(selectedCurrency)
            return
        }

        if (searchedCurrency) {
            setDefaultCurrency(searchedCurrency)
            localStorage.setItem(localStorageKeys.SELECTED_CURRENCY, searchedCurrency)
            return
        }
        setDefaultCurrency(Customer.getCurrencyByCustomerTimezone())
    }

    const limitsDataExists = !isFetching && returnPlansData(plansLimits?.monitoring)?.length && returnPlansData(plansLimits?.statuspage)?.length

    useEffect(() => {
        if (fetchedPlans?.length && coupon && coupon?.price_ids?.length === 1) {
            const plan = fetchedPlans?.find(item => item?.product_id === coupon?.price_ids[0])
            upgradePlan(plan?.plan, plan?.interval)
        }
        setShowLoader()
    }, [coupon, fetchedPlans])

    useEffect(() => {
        const coupon = searchParams.get('coupon')

        if (coupon) {
            localStorage.setItem('coupon', coupon)
        }
        !isSelfHosted() && dispatch(getSubscriptions(false, true))
        dispatch(getPlanLimits(navigate, isCurrentPlanAppsumo))
        selectDefaultCurrency()
    }, [])

    useEffect(() => {
        if (!hash) {
            window.location.hash = MONITORING_HASH
        }
    }, [location?.href])

    useEffect(() => {
        isAuthenticated && fetchPlans()
    }, [isAuthenticated])

    useEffect(() => {
        if (searchedPlan && searchParams.get('interval') && fetchedPlans?.length > 0) {
            const plan =  plans.find(plan => plan.key === searchedPlan)
            upgradePlan(plan?.id, searchParams.get('interval'))
        }
    }, [fetchedPlans, plansLimits])

    const currencySelectValue = data.currency_options.find(option => option.value === defaultCurrency)
    return (
        <>
            <Modal
                title={text.modal_title}
                className="proration-modal"
                closeModal={() => setIsModalActive(false)}
                isOpen={isModalActive}>
                <UpgradeModal
                    setModal={setIsModalActive}
                    plansData={allPlans}
                    modal={isModalActive}
                    setShowLoader={setShowLoader}
                    findPlanData={findPlanData}
                    findPlanPriceId={findPlanPriceId}
                    paymentInterval={paymentInterval}
                    getUpgradeIsEnabled={getUpgradeIsEnabled}
                    getPriceSymbol={getPriceSymbol}
                    isTrialPeriod={isTrialPeriod}
                    currency={defaultCurrency}
                />
            </Modal>
            {showLoader && <FullscreenLoader/>}
            <main className={classNames('main content-page plans', {
                dark: isDarkMode
            })}>
                <div className={isCurrentPlanAppsumo ? 'appsumo-content' : 'default-content'}>
                    <ResponsivePageTitle/>
                    <div className="content-title">
                        <div className="left-side">
                            <div>
                                <PageSubTitle>
                                    <p className={classNames('section', {
                                        dark: isDarkMode
                                    })}>{text.section}</p>
                                </PageSubTitle>
                                <PageTitle>
                                    <h1  className={classNames('page-title', {
                                        dark: isDarkMode
                                    })}>{text.title}</h1>
                                </PageTitle>
                            </div>
                        </div>
                    </div>
                    {!limitsDataExists ? (
                        <ContentLoader/>
                    ) : (
                        <div>
                            <div className={classNames('box-header', {
                                dark: isDarkMode
                            })}>
                                {!isSelfHosted() && <>
                                    <div className='d-flex'>
                                        <span className={`left-switch ${paymentInterval === MONTH_INTERVAL ? 'active' : ''}`}
                                            id="monthly-billing">{text.monthy_billings}</span>
                                        <span>
                                            <label className="switch" id="pricing-switcher">
                                                <input checked={paymentInterval === YEAR_INTERVAL} onChange={e => handleChange(e)}
                                                    type="checkbox"/>
                                                <span className="slider round"/>
                                            </label>
                                        </span>
                                        <span className={`right-switch ${paymentInterval === YEAR_INTERVAL ? 'active' : ''}`}
                                            id="annual-billing">{text.annual_billings}</span>
                                        <span className="switch-info">{text.save_percent}</span>
                                    </div>
                                    <span className='select-currency' id='currency'>
                                        <CustomSelect
                                            options={data.currency_options}
                                            value={currencySelectValue}
                                            onChange={(_, data) => {
                                                setDefaultCurrency(data.value)
                                                localStorage.setItem(localStorageKeys.SELECTED_CURRENCY, data.value)
                                            }}
                                        /></span></>}
                            </div>
                            <div className='plans-wrapper'>
                                <div className='plans-container'>
                                    <div className={classNames('sticky-part outside', {
                                        dark: isDarkMode
                                    })}>
                                        <div className="plans-header-top">
                                            <div className="no-center left-0">
                                                <p className="left-title">{text.compare_plans}</p>
                                            </div>
                                            {returnPlansData(plansLimits?.monitoring)?.map((plan, index) => {
                                                return (
                                                    <div key={index} className="plans-cost bg-blue top-24">
                                                        <h3>{plan.name}</h3>
                                                        <p>{plan.desc}</p>
                                                        {returnCurrentPlanBadge(plan)}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        {!isSelfHosted() && <div className="plans-header-top">
                                            <div className="no-center"/>
                                            {generatePlansPrice()}
                                        </div>}
                                        {!isSelfHosted() && <div className="plans-header-top">
                                            <div className="no-center">
                                                <p className="left-items"/>
                                            </div>
                                            {generatePlansButton()}
                                        </div>}
                                    </div>
                                    <div className="content-body">
                                        <div className="plans-body">
                                            <div className={classNames('sticky-part inside', {
                                                dark: isDarkMode
                                            })}>
                                                <div className="plans-header-top">
                                                    <div className="no-center left-0">
                                                        <p className="left-title">{text.compare_plans}</p>
                                                    </div>
                                                    {returnPlansData(plansLimits?.monitoring)?.map((plan, index) => {
                                                        return (
                                                            <div key={index} className="plans-cost bg-blue top-24">

                                                                <h3>{plan.name}</h3>
                                                                <p>{plan.desc}</p>
                                                                {returnCurrentPlanBadge(plan)}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                                {!isSelfHosted() && <div className="plans-header-top">
                                                    <div className="no-center">
                                                        <p className="left-items">{text.monthly_price}</p>
                                                    </div>
                                                    {generatePlansPrice()}
                                                </div>}
                                                {!isSelfHosted() && <div className="plans-header-top">
                                                    <div className="no-center">
                                                        <p className="left-items"/>
                                                    </div>
                                                    {generatePlansButton()}
                                                </div>}
                                            </div>
                                            <div className="d-flex flex-column">
                                                {currentSection && returnPlansSection(currentSection)}
                                                {filteredPlansSections.map(section => (
                                                    section.hash !== hash && returnPlansSection(section)
                                                ))}

                                                <div className={classNames('plans-header', {
                                                    dark: isDarkMode
                                                })}>
                                                    <div className="plans-header-top">
                                                        <div className={classNames('no-center plan-title', {
                                                            'self-hosted': isSelfHosted()
                                                        })}>
                                                            <p className="left-sub-title">Other</p>
                                                        </div>
                                                        {returnPlansData(plansLimits?.other)?.map((plan, index) => (
                                                            <div key={index} className="plans-cost bg-blue" />
                                                        ))}
                                                    </div>
                                                    {sortAndExtractKeysAndTitles(plansLimits?.other?.[plansLimits?.other?.length - 1]?.limits)?.map((feature, index) => (
                                                        <div key={index} className="plans-header-top">
                                                            <div className="no-center">
                                                                <p className="left-items" dangerouslySetInnerHTML={{__html: feature.name}}/>
                                                            </div>
                                                            {returnPlansData(plansLimits?.other)?.map((plan, index) => (
                                                                <div key={index} className="bg-blue">
                                                                    <p>{returnFeatureValue(plan[feature.key])}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <Footer/>
            </main>
        </>
    )
}

export default Plans
